/**
 * The node palette taxonomy (spec §4.3) — organised by what the author is trying to DO, in four
 * groups: Capability families (the 12 from the registry), Human tasks, AI primitives, Flow control.
 * Each entry knows how to materialise a schema-shaped IR node at a target lane (stage), so a drop or
 * a keyboard place produces a valid-ish draft node the compiler then judges (spec §7.1).
 *
 * RESERVED AI kinds render but cannot be placed (the Wave-3/5 compiler rejects them, OL-IR-002);
 * llmTask + confidenceGate are IMPLEMENTED (v1.1). The palette lists families; the capability string
 * resolves as `family.vN` against the tenant's entitled providers (jurisdiction-gated server-side).
 */
import type { LifecycleStage, NodeKind } from '@/theme/tokens';
import { NODE_KIND_META } from '@/theme/tokens';
import type { FamilyCatalogueEntry } from '@/api/types';
import type { FlowNode } from '@/flow-ir/types';

export type PaletteGroup = 'capability' | 'human' | 'ai' | 'control';

export interface PaletteEntry {
  /** Stable palette key (also the drag dataTransfer payload). */
  key: string;
  group: PaletteGroup;
  label: string;
  description: string;
  kind: NodeKind;
  glyph: string;
  accent: string;
  /** RESERVED — rendered but not placeable (compiler-rejected). */
  reserved?: boolean;
  /** Materialise a fresh IR node of this entry at the given id + lane. */
  make: (id: string, stage: LifecycleStage) => FlowNode;
}

export const GROUP_META: Record<PaletteGroup, { label: string; hint: string }> = {
  capability: { label: 'Capability families', hint: 'Stateless leaves the flow calls (CORPUS-04)' },
  human: { label: 'Human tasks', hint: 'Review, RFI, manual verification — assigned, SLA’d' },
  ai: { label: 'AI primitives', hint: 'Governed AI nodes — draft-only, fallback-typed' },
  control: { label: 'Flow control', hint: 'Decisions, gateways, sub-flows, scoring, terminals' },
};

/** Human-task presets (all kind=humanTask; differ by assignment + decision contract). */
const HUMAN_ENTRIES: PaletteEntry[] = [
  {
    key: 'human.review',
    group: 'human',
    label: 'Review / approval',
    description: 'Maker-checker approval task with a typed decision.',
    kind: 'humanTask',
    glyph: 'user',
    accent: NODE_KIND_META.humanTask.accent,
    make: (id, stage) => ({
      id,
      kind: 'humanTask',
      name: 'Review & approve',
      stage,
      reviewerPool: 'mlro-queue',
      sla: 'PT48H',
      decisionOptions: ['APPROVED', 'REJECTED'],
      makerChecker: true,
    }),
  },
  {
    key: 'human.rfi',
    group: 'human',
    label: 'RFI / outreach',
    description: 'Request-for-information / customer outreach task.',
    kind: 'humanTask',
    glyph: 'user',
    accent: NODE_KIND_META.humanTask.accent,
    make: (id, stage) => ({
      id,
      kind: 'humanTask',
      name: 'Request information',
      stage,
      reviewerPool: 'ops-queue',
      sla: 'PT72H',
      decisionOptions: ['PROVIDED', 'NO_RESPONSE'],
    }),
  },
  {
    key: 'human.verify',
    group: 'human',
    label: 'Manual verification',
    description: 'Human manual-verification task.',
    kind: 'humanTask',
    glyph: 'user',
    accent: NODE_KIND_META.humanTask.accent,
    make: (id, stage) => ({
      id,
      kind: 'humanTask',
      name: 'Manual verification',
      stage,
      reviewerPool: 'ops-queue',
      sla: 'PT24H',
      decisionOptions: ['PASS', 'FAILED'],
    }),
  },
];

/** AI primitives — llmTask + confidenceGate are implemented; the rest are RESERVED (shown, not placeable). */
const AI_ENTRIES: PaletteEntry[] = [
  {
    key: 'ai.llmTask',
    group: 'ai',
    label: 'LLM task',
    description: 'Governed LLM step — path-typed fallback + prompt (ADR-006).',
    kind: 'llmTask',
    glyph: 'spark',
    accent: NODE_KIND_META.llmTask.accent,
    make: (id, stage) => ({
      id,
      kind: 'llmTask',
      name: 'LLM task',
      stage,
      pathType: 'ASSISTIVE',
      fallback: { mode: 'DETERMINISTIC_VALUE', value: {} },
      prompt: { id: 'prompt-1', template: 'Summarise the case for review.' },
      params: {},
    }),
  },
  {
    key: 'ai.confidenceGate',
    group: 'ai',
    label: 'Confidence gate',
    description: 'Gate an upstream LLM task’s recorded confidence (PASS / ESCALATE).',
    kind: 'confidenceGate',
    glyph: 'gate',
    accent: NODE_KIND_META.confidenceGate.accent,
    make: (id, stage) => ({
      id,
      kind: 'confidenceGate',
      name: 'Confidence gate',
      stage,
      source: '',
      threshold: 0.8,
    }),
  },
  ...(
    ['agentLoop', 'humanReviewTask', 'shadowEval', 'retrievalTask', 'memoryTask', 'policyGuard', 'agentNegotiation'] as NodeKind[]
  ).map((kind): PaletteEntry => {
    const meta = NODE_KIND_META[kind];
    return {
      key: `ai.${kind}`,
      group: 'ai',
      label: meta.label,
      description: 'RESERVED AI primitive — the compiler rejects it (OL-IR-002, Wave-5).',
      kind,
      glyph: meta.glyph,
      accent: meta.accent,
      reserved: true,
      make: (id, stage) => ({ id, kind, name: meta.label, stage }),
    };
  }),
];

/** Flow-control entries — the closed IR grammar (no invented kinds). */
const CONTROL_ENTRIES: PaletteEntry[] = [
  {
    key: 'control.decision',
    group: 'control',
    label: 'Decision',
    description: 'Deterministic CEL branch (ADR-011) with a closed outcome set.',
    kind: 'decision',
    glyph: 'branch',
    accent: NODE_KIND_META.decision.accent,
    make: (id, stage) => ({
      id,
      kind: 'decision',
      name: 'Decision',
      stage,
      expression: 'true',
      outcomes: ['PASS', 'FAILED'],
    }),
  },
  {
    key: 'control.parallel',
    group: 'control',
    label: 'Parallel split',
    description: 'Fan a case out to concurrent branches.',
    kind: 'parallel',
    glyph: 'split',
    accent: NODE_KIND_META.parallel.accent,
    make: (id, stage) => ({ id, kind: 'parallel', name: 'Parallel split', stage }),
  },
  {
    key: 'control.join',
    group: 'control',
    label: 'Join',
    description: 'Re-converge branches (ALL / ANY / QUORUM).',
    kind: 'join',
    glyph: 'merge',
    accent: NODE_KIND_META.join.accent,
    make: (id, stage) => ({ id, kind: 'join', name: 'Join', stage, policy: 'ALL' }),
  },
  {
    key: 'control.subFlow',
    group: 'control',
    label: 'Sub-flow',
    description: 'Run a referenced flow per entity role (hierarchical node).',
    kind: 'subFlow',
    glyph: 'partition',
    accent: NODE_KIND_META.subFlow.accent,
    make: (id, stage) => ({
      id,
      kind: 'subFlow',
      name: 'Sub-flow',
      stage,
      flow: { id: 'sub-flow', version: 1 },
      entityBinding: 'HAS_RELATED',
    }),
  },
  {
    key: 'control.dynamicBranch',
    group: 'control',
    label: 'Fan-out',
    description: 'One child flow per item in a list from evidence, such as each director. Limits are required.',
    kind: 'dynamicBranch',
    glyph: 'partition',
    accent: NODE_KIND_META.dynamicBranch.accent,
    make: (id, stage) => ({
      id,
      kind: 'dynamicBranch',
      name: 'Fan-out',
      stage,
      over: '',
      flow: { id: '', version: 1 },
      entityBinding: 'HAS_DIRECTOR',
      policy: 'ALL',
    }),
  },
  {
    key: 'control.multiSourceScoring',
    group: 'control',
    label: 'Multi-source scoring',
    description: 'Weighted evidence aggregation to a threshold.',
    kind: 'multiSourceScoring',
    glyph: 'scoring',
    accent: NODE_KIND_META.multiSourceScoring.accent,
    make: (id, stage) => ({
      id,
      kind: 'multiSourceScoring',
      name: 'Score',
      stage,
      sources: [
        { source: 'screening', weight: 0.5 },
        { source: 'risk', weight: 0.5 },
      ],
      threshold: 0.7,
    }),
  },
  {
    key: 'control.terminal',
    group: 'control',
    label: 'Terminal',
    description: 'A terminal outcome for the case.',
    kind: 'terminal',
    glyph: 'flag',
    accent: NODE_KIND_META.terminal.accent,
    make: (id, stage) => ({ id, kind: 'terminal', name: 'Outcome', stage, outcome: 'COMPLETED' }),
  },
];

/** Build the capability-family entries from the registry catalogue (the 12 families). */
export function capabilityEntries(families: FamilyCatalogueEntry[]): PaletteEntry[] {
  return families.map((f) => ({
    key: `capability.${f.family}.v${f.version}`,
    group: 'capability',
    label: prettyFamily(f.family),
    description: f.description,
    kind: 'capability',
    glyph: NODE_KIND_META.capability.glyph,
    accent: NODE_KIND_META.capability.accent,
    make: (id, stage) => ({
      id,
      kind: 'capability',
      name: prettyFamily(f.family),
      stage,
      capability: `${f.family}.v${f.version}`,
      config: {},
    }),
  }));
}

/** A tolerant fallback catalogue (integration-fabric §2.1) so the palette works offline / pre-API. */
export const FALLBACK_FAMILIES: FamilyCatalogueEntry[] = [
  { family: 'screening', version: 1, description: 'Sanctions / PEP / adverse-media screening.' },
  { family: 'identity-verification', version: 1, description: 'Identity / ID-document verification.' },
  { family: 'document-analysis', version: 1, description: 'Document collection + analysis.' },
  { family: 'biometric', version: 1, description: 'Biometric / liveness check.' },
  { family: 'registry-lookup', version: 1, description: 'Company / statutory registry lookup (CIN/GST).' },
  { family: 'risk-scoring', version: 1, description: 'Risk scoring / rating.' },
  { family: 'communication', version: 1, description: 'Contact verification / OTP / outreach.' },
  { family: 'e-sign', version: 1, description: 'Electronic signature.' },
  { family: 'video-kyc', version: 1, description: 'Video KYC (vCIP) session.' },
  { family: 'banking-verification', version: 1, description: 'Bank / payment instrument verification.' },
  { family: 'forms', version: 1, description: 'Forms / questionnaire capture.' },
  { family: 'transaction-monitoring', version: 1, description: 'Transaction monitoring / alerting.' },
];

function prettyFamily(family: string): string {
  return family
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/** The static (non-capability) palette groups. */
export const STATIC_ENTRIES: PaletteEntry[] = [...HUMAN_ENTRIES, ...AI_ENTRIES, ...CONTROL_ENTRIES];

/** The full palette given a resolved family catalogue. */
export function buildPalette(families: FamilyCatalogueEntry[]): PaletteEntry[] {
  return [...capabilityEntries(families), ...STATIC_ENTRIES];
}
